import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useNotifications } from "@/shared/composables/useNotifications.composable";
import { translateError } from "@/shared/helper/translateError.helper";
import type { RegistrationFormData } from "@/app/registration-app/user/new-registration/@types/RegistrationFormData";
import { registerUser } from "@/app/registration-app/user/new-registration/services/registerUser.service";

const isUserAlreadyExistsError = (error: string) => {
  return error === "USER_ALREADY_EXISTS";
};

export const useRegisterUser = (
  onActionFinished: () => void,
  onUserAlreadyExistsError: () => void,
) => {
  const { t } = useI18n();
  const { addSuccessSnackbar, addErrorSnackbar } = useNotifications();

  const isRegisteringUser = ref(false);

  const onRegisteringUser = async (registrationData: RegistrationFormData) => {
    try {
      isRegisteringUser.value = true;
      await registerUser({
        companyName: registrationData.companyName,
        salutation: registrationData.salutation,
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        email: registrationData.email,
        newsletterConsent: registrationData.newsletter,
        captcha: registrationData.captcha,
      });
      addSuccessSnackbar(t("registration.shared.successNotification"), 2000);
      onActionFinished();
    } catch (error) {
      const errorString = error as string;
      if (isUserAlreadyExistsError(errorString)) {
        onUserAlreadyExistsError();
      } else {
        addErrorSnackbar(translateError(errorString));
      }
    } finally {
      isRegisteringUser.value = false;
    }
  };

  return {
    isRegisteringUser,
    onRegisteringUser,
  };
};
